import React, { useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

/**
 * StandardReadView - Universal Read Engine
 * 
 * Authority: READ_VIEW_LAW.md + RV_NAV_CAPABILITY_LAW.md
 * Enforcement: Gate 15 (UI Detail View Capability)
 * 
 * This is the ONLY allowed detail view component.
 * No feature page may render its own inside view.
 * 
 * Features:
 * - Schema-driven section rendering (no hardcoded fields)
 * - Universal Escape key handling (NAV class)
 * - Fallback resolution per section
 */

// Section value resolver (pure function)
const resolveSectionValue = (data, section) => {
    // Try primary field
    if (data[section.field] !== undefined && data[section.field] !== null && data[section.field] !== '') {
        return data[section.field];
    }

    // Try fallbacks in order
    for (const fallbackField of section.fallback || []) {
        if (data[fallbackField] !== undefined && data[fallbackField] !== null && data[fallbackField] !== '') {
            return data[fallbackField];
        }
    }

    return null; // Will render "Not available"
};

// Section renderer by type
const renderSectionContent = (value, renderType) => {
    if (value === null || value === undefined) {
        return <p className="text-sm text-muted-foreground italic">Not available</p>;
    }

    switch (renderType) {
        case 'list':
            if (!Array.isArray(value) || value.length === 0) {
                return <p className="text-sm text-muted-foreground italic">None</p>;
            }
            return (
                <ul className="list-disc list-inside space-y-1">
                    {value.map((item, idx) => (
                        <li key={idx} className="text-sm text-foreground">{String(item)}</li>
                    ))}
                </ul>
            );

        case 'object':
            if (typeof value !== 'object' || Object.keys(value).length === 0) {
                return <p className="text-sm text-muted-foreground italic">None</p>;
            }
            return (
                <dl className="grid grid-cols-1 gap-2">
                    {Object.entries(value).map(([key, val]) => (
                        <div key={key} className="flex gap-2">
                            <dt className="text-xs text-muted-foreground font-medium min-w-[100px]">{key}:</dt>
                            <dd className="text-sm text-foreground">{String(val)}</dd>
                        </div>
                    ))}
                </dl>
            );

        case 'json':
            return (
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
                    {JSON.stringify(value, null, 2)}
                </pre>
            );

        case 'text':
        default:
            const textValue = String(value);
            return (
                <div className="prose prose-sm max-w-none text-foreground">
                    {textValue.split('\\n').map((paragraph, idx) => (
                        <p key={idx} className="mb-3 last:mb-0 whitespace-pre-wrap break-words">
                            {paragraph}
                        </p>
                    ))}
                </div>
            );
    }
};

export function StandardReadView({
    schema,
    data,
    onClose
}) {
    // Universal Escape key handling (NAV class)
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation(); // Prevent conflicts with other overlays
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Schema Validation
    if (!data) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="standard-read-view">
                <div className="bg-card border border-destructive rounded-lg p-6 max-w-md mx-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold text-destructive">Data Error</h3>
                        {onClose && (
                            <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
                                <X size={20} />
                            </Button>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground">No data provided</p>
                </div>
            </div>
        );
    }

    const missing = schema.required.filter(field => !data[field]);
    if (missing.length > 0) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="standard-read-view">
                <div className="bg-card border border-destructive rounded-lg p-6 max-w-md mx-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold text-destructive">Data Error</h3>
                        {onClose && (
                            <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
                                <X size={20} />
                            </Button>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground">Missing required fields: {missing.join(', ')}</p>
                </div>
            </div>
        );
    }

    // Title (required field)
    const title = data.title || schema.fallbacks?.title || 'Untitled';

    // Schema-driven sections (RV class)
    const sections = schema.sections || [];

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="standard-read-view">
            <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-border">
                    <div className="flex-1 min-w-0">
                        <h2 className="font-heading font-semibold text-xl text-foreground mb-2 break-words">
                            {title}
                        </h2>
                    </div>
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            aria-label="Close"
                            className="ml-4 text-muted-foreground hover:text-foreground"
                        >
                            <X size={24} />
                        </Button>
                    )}
                </div>

                {/* Body (Scrollable) - Schema-driven sections */}
                <div className="flex-1 overflow-y-auto p-6">
                    {sections.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No sections defined in schema</p>
                    ) : (
                        <div className="space-y-6">
                            {sections.map((section) => {
                                const value = resolveSectionValue(data, section);

                                // Skip empty sections unless they're important
                                if (value === null && section.id !== 'description' && section.id !== 'summary') {
                                    return null;
                                }

                                return (
                                    <div key={section.id} data-testid="read-view-section" className="space-y-2">
                                        <h3 className="font-heading font-medium text-sm text-muted-foreground tracking-wide">
                                            {section.label}
                                        </h3>
                                        <div className="pl-0">
                                            {renderSectionContent(value, section.renderType || 'text')}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-border">
                    <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                        {data.created_at && (
                            <span>Created: {new Date(data.created_at).toLocaleDateString()}</span>
                        )}
                        {data.updated_at && (
                            <span>Updated: {new Date(data.updated_at).toLocaleDateString()}</span>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        Close (Esc)
                    </Button>
                </div>
            </div>
        </div>
    );
}
